import { AppendReason, RemoveReason, StringRealProperty, UpdateWatcher, ViewCompute, ViewUpdate, VList, VObjectInList, VObjectInListWithParent } from "@1oo4/miv";
import { VSectionDoc } from "./views";
import { CodeLanguage, CodeLanguageProperty, MCode, MCodeTab, MContent, MPicture, MRow, MTable, MText } from "../../shared/model/mdoc";
import { VRichTextItem } from "./richtexts";
import { MRichTextItem } from "../../shared/model/richtext";
import { VRow } from "./tables";
import { PictureManager } from "../../shared/uitools/picturetools";
import { TabControl, TabItem } from "./tabcontrol";
import highlightAPI from 'highlight.js/lib/core';
import highlightCS from 'highlight.js/lib/languages/csharp';
import highlightTS from 'highlight.js/lib/languages/typescript';
import highlightXML from 'highlight.js/lib/languages/xml';
import highlightCSS from 'highlight.js/lib/languages/css';
import highlightSQL from 'highlight.js/lib/languages/sql';
import highlightJSON from 'highlight.js/lib/languages/json';

export abstract class VContent extends VObjectInListWithParent<VSectionDoc> {

    private readonly container: HTMLDivElement;
    private initialized_content: boolean = false;

    constructor(m: MContent, parent: VSectionDoc) {
        super(m, parent);
        this.container = document.createElement('div');
    }

    abstract getModel(): MContent;
    abstract getView(): HTMLElement;

    protected AppendToGUI(ar: AppendReason, vu: ViewUpdate): void {
        if (!this.initialized_content) {
            this.initialized_content = true;
            this.container.appendChild(this.getView());
            this.InitializeOnFirstAppend(vu);
        }
        this.Parent.panelContent.appendChild(this.container);
    }
    protected InsertIntoGUI(index: number, vu: ViewUpdate): void {
        this.Parent.panelContent.insertBefore(this.container, this.Parent.panelContent.children[index]);
    }
    protected RemoveFromGUI(rr: RemoveReason, vu: ViewUpdate): void {
        this.Parent.panelContent.removeChild(this.container);
        if (rr == RemoveReason.DESTROYED) {
            this.Destroyed(vu);
        }
    }
    protected Destroyed(vu: ViewUpdate): void { }
    protected InitializeOnFirstAppend(vu: ViewUpdate): void { }

    protected ModelUpdated(vu: ViewUpdate): void {
        const m = this.getModel();
        if (vu.IsChanged(m.LanguageFlag)) {
            let className: string;
            if (m.LanguageFlag.Value == CodeLanguage.CS) {
                className = "content_container_cs";
            } else if (m.LanguageFlag.Value == CodeLanguage.TS) {
                className = "content_container_ts";
            } else {
                className = "content_container";
            }
            this.container.className = className;
        }
        this.ContentUpdated(vu);
    }
    protected ContentUpdated(vu: ViewUpdate): void { }

    abstract focusLanguage(lang: CodeLanguage): void;
}

export class VText extends VContent {
    private readonly mText: MText;
    readonly paragraph: HTMLParagraphElement;
    private readonly richText: VList<VRichTextItem, MRichTextItem>;

    constructor(m: MText, parent: VSectionDoc) {
        super(m, parent);
        this.mText = m;
        this.paragraph = document.createElement("p");
        this.richText = new VList(m.RichText, this, x => x.makeView(this.paragraph, this));
    }
    getModel(): MText {
        return this.mText;
    }
    getView(): HTMLElement {
        return this.paragraph;
    }

    focusLanguage(lang: CodeLanguage): void {
        // rien
    }
}

export class VPicture extends VContent {
    private readonly mPic: MPicture;
    private readonly image: PictureManager;

    constructor(m: MPicture, parent: VSectionDoc) {
        super(m, parent);
        this.mPic = m;
        this.image = new PictureManager();
    }
    getModel(): MPicture {
        return this.mPic;
    }
    getView(): HTMLElement {
        return this.image.image;
    }

    protected ContentUpdated(vu: ViewUpdate): void {
        if (vu.IsChanged(this.mPic.Data)) {
            this.image.setPicture(this.mPic.Data);
        }
    }

    focusLanguage(lang: CodeLanguage): void {
        // rien
    }
}

highlightAPI.registerLanguage("csharp", highlightCS);
highlightAPI.registerLanguage("typescript", highlightTS);
highlightAPI.registerLanguage("xml", highlightXML);
highlightAPI.registerLanguage("css", highlightCSS);
highlightAPI.registerLanguage("sql", highlightSQL);
highlightAPI.registerLanguage("json", highlightJSON);

class VCodeTab extends VObjectInList<MCodeTab, VCode> {

    readonly view: TabItem;
    private langCode: string = "typescript";
    private readonly codeBlock: HTMLElement;

    constructor(m: MCodeTab, parent: VCode) {
        super(m, parent);
        this.view = document.createElement('tab-item') as TabItem;

        this.codeBlock = document.createElement('code');
        this.codeBlock.className = 'hljs';
        const pre = document.createElement('pre') as HTMLPreElement;
        pre.className = 'hljs';
        pre.appendChild(this.codeBlock);
        this.view.appendChild(pre);
    }

    protected AppendToGUI(ar: AppendReason, vu: ViewUpdate): void {
        this.Parent.view.appendChild(this.view);
    }
    protected InsertIntoGUI(index: number, vu: ViewUpdate): void {
        this.Parent.view.insertBefore(this.view, this.Parent.view.children[index]);
    }
    protected RemoveFromGUI(rr: RemoveReason, vu: ViewUpdate): void {
        this.Parent.view.removeChild(this.view);
    }

    private static getHeaderName(lang: CodeLanguage): string {
        switch (lang) {
            case CodeLanguage.CS: return "C#";
            case CodeLanguage.TS: return "TypeScript";
            case CodeLanguage.XAML: return "XAML";
            case CodeLanguage.HTML: return "HTML";
            case CodeLanguage.CSS: return "CSS";
            case CodeLanguage.SQL: return "SQL";
            case CodeLanguage.JSON: return "JSON";
            default: return "pseudo-code";
        }
    }

    private static getLangCode(lang: CodeLanguage): string {
        switch (lang) {
            case CodeLanguage.CS: return "csharp";
            case CodeLanguage.TS: return "typescript";
            case CodeLanguage.XAML: return "xml";
            case CodeLanguage.HTML: return "xml";
            case CodeLanguage.CSS: return "css";
            case CodeLanguage.SQL: return "sql";
            case CodeLanguage.JSON: return "json";
            default: return "typescript";
        }
    }

    protected ModelUpdated(vu: ViewUpdate): void {
        let langChange = false;
        if (vu.IsChanged(this.Model.Language)) {
            langChange = true;
            this.view.header = VCodeTab.getHeaderName(this.Model.Language.Value);
            this.langCode = VCodeTab.getLangCode(this.Model.Language.Value);
            this.Parent.updateLanguages(vu);
        }
        if (langChange || vu.IsChanged(this.Model.Code)) {
            this.codeBlock.innerHTML = highlightAPI.highlight(this.Model.Code.Value, { language: this.langCode }).value;
        }
    }
}

export class VCode extends VContent {

    private readonly mCode: MCode;
    readonly view: TabControl;
    private readonly tabs: VList<VCodeTab, MCodeTab>;
    private contains_cs_and_ts: boolean = false;
    private contains_xaml_and_html: boolean = false;
    private last_update_languages: number = 0;

    constructor(m: MCode, parent: VSectionDoc) {
        super(m, parent);
        this.mCode = m;
        this.view = document.createElement('tab-control') as TabControl;
        this.tabs = new VList(m.Tabs, this, x => new VCodeTab(x, this));

        this.view.addEventListener('tabSelectionChanged', evt => {
            if (this.tabs.Count > 1) {
                for (let i = 0; i < this.tabs.Count; i++) {
                    const tab = this.tabs.Get(i);
                    if (tab.view == evt.detail.current) {
                        const lang = tab.Model.Language.Value;
                        if (((lang == CodeLanguage.CS || lang == CodeLanguage.TS) && this.contains_cs_and_ts) || ((lang == CodeLanguage.XAML || lang == CodeLanguage.HTML) && this.contains_xaml_and_html)) {
                            this.Parent.Parent.Parent.Parent.page.focusLanguage(lang);
                        }
                        break;
                    }
                }
            }
        })
    }

    getModel(): MCode {
        return this.mCode;
    }
    getView(): HTMLElement {
        return this.view;
    }

    protected ContentUpdated(vu: ViewUpdate): void {
        if (vu.IsChanged(this.mCode.Tabs)) {
            this.updateLanguages(vu);
        }
    }

    private getSelectedLanguage(): CodeLanguage {
        for (let i = 0; i < this.tabs.Count; i++) {
            const tab = this.tabs.Get(i);
            if (tab.view == this.view.SelectedItem) {
                return tab.Model.Language.Value;
            }
        }
        return CodeLanguage.PSEUDO;
    }

    protected InitializeOnFirstAppend(vu: ViewUpdate): void {
        this.updateLanguages(vu);
        if (this.Parent.Parent.Parent.Parent.page.focusCSratherThanTS) {
            if (this.contains_cs_and_ts) {
                this.selectTabBasedOnLanguage(CodeLanguage.CS);
            } else if (this.contains_xaml_and_html) {
                this.selectTabBasedOnLanguage(CodeLanguage.XAML);
            }
        } else {
            if (this.contains_cs_and_ts) {
                this.selectTabBasedOnLanguage(CodeLanguage.TS);
            } else if (this.contains_xaml_and_html) {
                this.selectTabBasedOnLanguage(CodeLanguage.HTML);
            }
        }
    }
    private selectTabBasedOnLanguage(lang: CodeLanguage) {
        for (let i = 0; i < this.tabs.Count; i++) {
            const tab = this.tabs.Get(i);
            if (tab.Model.Language.Value == lang) {
                this.view.SelectItemWithoutTriggeringEvent(tab.view);
                break;
            }
        }
    }

    updateLanguages(vu: ViewUpdate) {
        if (this.last_update_languages != vu.Counter) {
            this.last_update_languages = vu.Counter;
            let has_cs = false;
            let has_ts = false;
            let has_xaml = false;
            let has_html = false;
            for (let i = 0; i < this.tabs.Count; i++) {
                const lang = this.tabs.Get(i).Model.Language.Value;
                if (lang == CodeLanguage.CS) {
                    has_cs = true;
                } else if (lang == CodeLanguage.TS) {
                    has_ts = true;
                } else if (lang == CodeLanguage.XAML) {
                    has_xaml = true;
                } else if (lang == CodeLanguage.HTML) {
                    has_html = true;
                }
            }
            this.contains_cs_and_ts = has_cs && has_ts;
            this.contains_xaml_and_html = has_xaml && has_html;
        }
    }


    focusLanguage(lang: CodeLanguage): void {
        if (lang == CodeLanguage.CS || lang == CodeLanguage.TS || lang == CodeLanguage.XAML || lang == CodeLanguage.HTML) {
            const current = this.getSelectedLanguage();
            if (this.contains_cs_and_ts) {
                if (lang == CodeLanguage.CS || lang == CodeLanguage.XAML) {
                    if (current != CodeLanguage.CS && current != CodeLanguage.XAML)
                        this.selectTabBasedOnLanguage(CodeLanguage.CS);
                } else {
                    if (current != CodeLanguage.TS && current != CodeLanguage.HTML && current != CodeLanguage.CSS)
                        this.selectTabBasedOnLanguage(CodeLanguage.TS);
                }
            } else if (this.contains_xaml_and_html) {
                if (lang == CodeLanguage.CS || lang == CodeLanguage.XAML) {
                    if (current != CodeLanguage.CS && current != CodeLanguage.XAML)
                        this.selectTabBasedOnLanguage(CodeLanguage.XAML);
                } else {
                    if (current != CodeLanguage.TS && current != CodeLanguage.HTML && current != CodeLanguage.CSS)
                        this.selectTabBasedOnLanguage(CodeLanguage.HTML);
                }
            }
        }
    }
}

export class VTable extends VContent {
    private readonly mTable: MTable;
    readonly view: HTMLTableElement;
    private readonly rows: VList<VRow, MRow>;
    private last_colwidth = 0;

    constructor(m: MTable, parent: VSectionDoc) {
        super(m, parent);
        this.mTable = m;
        this.view = document.createElement("table");
        this.rows = new VList(m.Rows, this, x => new VRow(x, this));
        this.mTable.ColumnsChanged.Watch(this);
    }
    getModel(): MTable {
        return this.mTable;
    }
    getView(): HTMLElement {
        return this.view;
    }

    focusLanguage(lang: CodeLanguage): void {
        // rien
    }

    protected Destroyed(vu: ViewUpdate): void {
        this.mTable.ColumnsChanged.Unwatch(this);
    }

    protected WatcherTriggered(uw: UpdateWatcher, vu: ViewUpdate): void {
        if (uw == this.mTable.ColumnsChanged) {
            this.columnWidth(vu);
        }
    }

    protected ContentUpdated(vu: ViewUpdate): void {
        if (vu.IsChanged(this.mTable.Rows) || this.mTable.ColumnsChanged.IsTriggered) {
            this.columnWidth(vu);
        }
    }

    private columnWidth(vu: ViewUpdate) {
        if (this.last_colwidth != vu.Counter) {
            this.last_colwidth = vu.Counter;
            if (this.rows.Count > 0) {
                const firstRow = this.rows.Get(0);
                let multiplier = 0;
                for (let c = 0; c < this.mTable.Columns.Count; c++) {
                    const width = this.mTable.Columns.Get(c).Width.Value;
                    if (width > 0) multiplier += Number(width);
                }
                if (multiplier > 0) multiplier = 100 / multiplier;
                else multiplier = 1;
                for (let i = 0; i < firstRow.cells.Count; i++) {
                    const cell = firstRow.cells.Get(i).view;
                    const column = this.mTable.Columns.Get(i);
                    if (column === undefined || column.Width.Value <= 0) {
                        cell.style.removeProperty("width");
                    } else {
                        cell.style.width = Math.round(Number(column.Width.Value) * multiplier) + "%";
                    }
                }
            }
        }
    }
}